import React, { useState, useEffect } from 'react';
import { Container, Table, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import CryptoIcon from './CryptoIcons';
import './RecentTransactions.css';

interface Transaction {
  id: string;
  time: string;
  fromAmount: number;
  fromCurrency: string;
  toAmount: number;
  toCurrency: string;
}

interface ExchangeRates {
  [key: string]: number;
}

// Add supported currencies
const CURRENCIES = [
  'BTC',
  'ETH',
  'USDTERC20',
  'USDC',
  'LTC',
  'MATIC',
  'SOL',
  'DOT',
  'AVAX'
];

interface CurrencyInfo {
  ticker: string;
  name: string;
  image: string;
}

const RecentTransactions: React.FC = () => {
  const [rates, setRates] = useState<ExchangeRates>({});
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [currencyImages, setCurrencyImages] = useState<{ [key: string]: string }>({});

  // Fetch exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        const response = await fetch(
          'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum,litecoin,tether,usd-coin,binancecoin,matic-network,solana,polkadot,avalanche-2&vs_currencies=usd'
        );
        const data = await response.json();
        
        setRates({
          BTC: data.bitcoin.usd,
          ETH: data.ethereum.usd,
          LTC: data.litecoin.usd,
          USDTERC20: data.tether.usd,
          USDC: data['usd-coin'].usd,
          BNB: data.binancecoin.usd,
          MATIC: data['matic-network'].usd,
          SOL: data.solana.usd,
          DOT: data.polkadot.usd,
          AVAX: data['avalanche-2'].usd
        });
      } catch (error) {
        console.error('Error fetching rates:', error);
      }
    };
  
    fetchRates();
    const interval = setInterval(fetchRates, 30000);
    return () => clearInterval(interval);
  }, []);

  // Add currency images fetch with proper ticker mapping
  useEffect(() => {
    const fetchCurrencyImages = async () => {
      try {
        const response = await fetch('https://api.changenow.io/v1/currencies?active=true');
        const data = await response.json();
        const images = data.reduce((acc: { [key: string]: string }, curr: CurrencyInfo) => {
          // Handle special cases for ticker mapping
          const ticker = curr.ticker.toUpperCase();
          
          // Map USDT to USDTERC20
          if (ticker === 'USDT') {
            acc['USDTERC20'] = curr.image;
          } 
          // Map BNB variants (BNB, BNB_BSC, BSC_BNB)
          else if (ticker === 'BNB' || ticker === 'BNB_BSC' || ticker === 'BSC_BNB' || ticker === 'BSC') {
            acc['BNB'] = curr.image;
          }
          // Handle all other currencies
          else {
            acc[ticker] = curr.image;
          }
          
          return acc;
        }, {});
        console.log('Currency Images:', images); // For debugging
        setCurrencyImages(images);
      } catch (error) {
        console.error('Error fetching currency images:', error);
      }
    };

    fetchCurrencyImages();
  }, []);

  // Update the image rendering part
  // Update generateRandomTransaction to use more realistic amounts
  const generateRandomTransaction = () => {
    const fromCurrency = CURRENCIES[Math.floor(Math.random() * CURRENCIES.length)];
    let toCurrency;
    do {
      toCurrency = CURRENCIES[Math.floor(Math.random() * CURRENCIES.length)];
    } while (toCurrency === fromCurrency);
  
    // More realistic amount ranges based on currency
    const getRandomAmount = (currency: string) => {
      switch (currency) {
        case 'BTC':
          return Math.random() * 2; // 0-2 BTC
        case 'ETH':
          return Math.random() * 10; // 0-10 ETH
        case 'USDTERC20':
        case 'USDC':
          return Math.random() * 10000; // 0-10000 USD
        case 'LTC':
          return Math.random() * 50; // 0-50 LTC
        case 'BNB':
          return Math.random() * 30; // 0-30 BNB
        case 'MATIC':
          return Math.random() * 5000; // 0-5000 MATIC
        case 'SOL':
          return Math.random() * 200; // 0-200 SOL
        case 'DOT':
          return Math.random() * 1000; // 0-1000 DOT
        case 'AVAX':
          return Math.random() * 300; // 0-300 AVAX
        default:
          return Math.random() * 100;
      }
    };
  
    const fromAmount = parseFloat(getRandomAmount(fromCurrency).toFixed(4));
    const toAmount = calculateExchangeAmount(fromAmount, fromCurrency, toCurrency);
  
    return {
      id: `tx${Date.now()}`,
      time: '1 sec',
      fromAmount,
      fromCurrency,
      toAmount,
      toCurrency
    };
  };

  // Calculate exchange amount based on real rates
  const calculateExchangeAmount = (fromAmount: number, fromCurrency: string, toCurrency: string): number => {
    if (!rates[fromCurrency] || !rates[toCurrency]) return fromAmount;
    return (fromAmount * rates[fromCurrency]) / rates[toCurrency];
  };

  useEffect(() => {
    if (Object.keys(rates).length > 0) {
      setTransactions([
        {
          id: 'tx1',
          time: '5 sec',
          fromAmount: 0.025,
          fromCurrency: 'BTC',
          toAmount: calculateExchangeAmount(0.025, 'BTC', 'USDC'),
          toCurrency: 'USDC'
        },
        {
          id: 'tx2',
          time: '1 min',
          fromAmount: 1.5,
          fromCurrency: 'LTC',
          toAmount: calculateExchangeAmount(1.5, 'LTC', 'USDTERC20'),
          toCurrency: 'USDTERC20'
        },
        {
          id: 'tx3',
          time: '3 min',
          fromAmount: 850,
          fromCurrency: 'USDTERC20',
          toAmount: calculateExchangeAmount(850, 'USDTERC20', 'ETH'),
          toCurrency: 'ETH'
        }
      ]);
    }
  }, [rates]);

  // Remove the duplicate useEffect for initial transactions
  
  const updateTransactionTimes = (txs: Transaction[]) => {
    return txs.map(tx => ({
      ...tx,
      time: tx.time === '1 sec' ? '5 sec' :
            tx.time === '5 sec' ? '1 min' :
            tx.time === '1 min' ? '3 min' : '5 min'
    }));
  };

  // Initialize and update transactions
  useEffect(() => {
    // Initial transactions
    if (Object.keys(rates).length > 0) {
      setTransactions([
        generateRandomTransaction(),
        generateRandomTransaction(),
        generateRandomTransaction()
      ]);
    }
  
    // Update every 10 seconds
    const interval = setInterval(() => {
      setTransactions(prevTxs => {
        const updatedTxs = updateTransactionTimes(prevTxs);
        if (updatedTxs.length >= 3) {
          updatedTxs.pop();
        }
        return [generateRandomTransaction(), ...updatedTxs.slice(0, 2)];
      });
    }, 10000);
  
    return () => clearInterval(interval);
  }, [rates]);

  const formatAmount = (amount: number): string => {
    return amount < 1 ? amount.toFixed(4) : amount.toFixed(2);
  };

  return (
    <div className="recent-transactions-section">
      <Container>
        <h2 className="text-center mb-4">
          <FontAwesomeIcon icon={faClock} className="me-2" />
          Recent Transactions
        </h2>
        <div className="transactions-table-container">
          <Table responsive className="transactions-table">
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id} className="transaction-row animate__animated animate__fadeIn">
                  <td className="time-cell">
                    <Badge bg="light" text="dark" className="time-badge">
                      <FontAwesomeIcon icon={faClock} className="me-1" />
                      {tx.time}
                    </Badge>
                  </td>
                  <td className="amount-cell">
                    <div className="d-flex align-items-center">
                      <span className="amount">{formatAmount(tx.fromAmount)}</span>
                      <div className="currency-icon-wrapper ms-2">
                        {currencyImages[tx.fromCurrency] ? (
                          <img 
                            src={currencyImages[tx.fromCurrency]} 
                            alt={tx.fromCurrency}
                            width={20}
                            height={20}
                            onError={(e) => {
                              e.currentTarget.onerror = null; // Prevent infinite loop
                              e.currentTarget.style.display = 'none';
                              const iconElement = e.currentTarget.parentElement;
                              if (iconElement) {
                                iconElement.innerHTML = `<div>${<CryptoIcon symbol={tx.fromCurrency} size={20} />}</div>`;
                              }
                            }}
                          />
                        ) : (
                          <CryptoIcon symbol={tx.fromCurrency} size={20} />
                        )}
                      </div>
                      <span className="currency-code ms-1">{tx.fromCurrency}</span>
                    </div>
                  </td>
                  <td className="arrow-cell">
                    <div className="arrow">→</div>
                  </td>
                  <td className="amount-cell">
                    <div className="d-flex align-items-center">
                      <span className="amount">{formatAmount(tx.toAmount)}</span>
                      <div className="currency-icon-wrapper ms-2">
                        {currencyImages[tx.toCurrency] ? (
                          <img 
                            src={currencyImages[tx.toCurrency]} 
                            alt={tx.toCurrency}
                            width={20}
                            height={20}
                          />
                        ) : (
                          <CryptoIcon symbol={tx.toCurrency} size={20} />
                        )}
                      </div>
                      <span className="currency-code ms-1">{tx.toCurrency}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Container>
    </div>
  );
};

export default RecentTransactions;